import './styles.css'
import { YEAR_MIN, YEAR_MAX, REPO_URL, HAS_BACKEND } from './config.js'
import { t, pick, locale, setLocale, applyDom, onLocaleChange, COMMUNITIES } from './i18n.js'
import {
  fetchStories, submitStory, reportStory, uploadFile,
  usingSampleData, toGeoJSON, toCSV, download,
} from './data.js'
import {
  initMap, map, renderStories, setKindVisible, setActive, flyToStory,
  setBasemap, setBasemapLang, basemap,
} from './map.js'
import { BASEMAPS, landsdLang } from './basemaps.js'
import * as coastline from './coastline.js'
import * as ui from './ui.js'

/* ── State ───────────────────────────────────────────────────────────────── */

const state = {
  all: [],
  kinds: { story: true, relocation: true, ecology: true, place: true },
  communities: new Set(),
  yearFrom: YEAR_MIN,
  yearTo: YEAR_MAX,
  query: '',
  selected: null,
}

/* A story with no dates should not vanish the moment someone touches the
   timeline. Oral history is frequently undated, and silently hiding those
   records would misrepresent the corpus as more precisely dated than it is —
   so undated records always pass the year filter. */
function inYearRange(s) {
  if (s.year_start == null && s.year_end == null) return true
  const from = s.year_start ?? s.year_end
  const to = s.year_end ?? s.year_start
  return to >= state.yearFrom && from <= state.yearTo
}

function matchesQuery(s) {
  if (!state.query) return true
  const q = state.query.toLowerCase()
  return [
    s.title_zh, s.title_en, s.narrative_zh, s.narrative_en,
    s.place_zh, s.place_en, s.dest_place_zh, s.dest_place_en,
    s.narrator_label, s.source_note, (s.species ?? []).join(' '),
  ].some((f) => f && String(f).toLowerCase().includes(q))
}

function visible() {
  return state.all.filter(
    (s) =>
      inYearRange(s) &&
      matchesQuery(s) &&
      (state.communities.size === 0 ||
        (s.communities ?? []).some((c) => state.communities.has(c)))
  )
}

function refresh() {
  const rows = visible()
  renderStories(rows)

  const counts = { story: 0, relocation: 0, ecology: 0, place: 0 }
  rows.forEach((s) => { counts[s.kind] = (counts[s.kind] ?? 0) + 1 })
  for (const k of Object.keys(counts)) {
    // Optional: a missing counter element is a cosmetic problem, and must not
    // be allowed to throw partway through boot and take the whole map with it.
    const el = document.getElementById(`count-${k}`)
    if (el) el.textContent = counts[k]
  }

  if (rows.length === 0 && state.all.length > 0) ui.toast(t('no_results'))
}

/* ── Boot ────────────────────────────────────────────────────────────────── */

async function boot() {
  applyDom()
  document.documentElement.lang = locale() === 'zh' ? 'zh-Hant-HK' : 'en'
  // No repository yet → drop the link rather than render one that 404s.
  // Optional-chained because boot() can re-run (HMR) after the node is gone,
  // and a TypeError here would abort the whole boot before the map ever loads.
  const repoLink = document.getElementById('repo-link')
  if (REPO_URL && repoLink) repoLink.href = REPO_URL
  else repoLink?.remove()

  // Start the map, but do not stand and wait for it. Only the story markers
  // actually need a loaded basemap; the filters, the layer list, the timeline
  // and the records themselves do not. Awaiting here put every one of them
  // behind a third-party tile server, so a slow or unreachable basemap left the
  // whole interface empty — no controls, no coastline list, no data — which is
  // far worse than a map that arrives late.
  const mapReady = initMap(onStoryClick)

  // Safe to run now: initMap creates both map objects synchronously and only
  // the 'load' event is deferred, so these handlers have something to bind to.
  buildCommunityChips()
  buildBasemapList()
  buildTimelineTicks()
  wireControls()

  await coastline.loadManifest()
  buildCoastlineList()

  try {
    state.all = await fetchStories()
  } catch (err) {
    ui.toast(err.message, true)
    state.all = []
  }

  // Markers are the one thing that genuinely needs the style in place. If the
  // basemap never arrives, say so and leave the rest of the app working.
  try {
    await mapReady
    refresh()
  } catch (err) {
    ui.toast(
      (locale() === 'zh' ? '底圖載入失敗：' : 'Base map failed: ') + err.message,
      true
    )
  }

  // The wording matters. These seed records are sourced and citable, so calling
  // them "fake" would be wrong — but they are documentary, drawn from censuses
  // and newspapers, and not a single one is testimony anybody gave. A visitor
  // who mistakes the two would misread the whole map.
  if (usingSampleData) {
    ui.toast(
      locale() === 'zh'
        ? '示範資料：以下為有出處的文獻記錄，並非口述訪談。每條均附引用。'
        : 'Sample data: sourced documentary records, not oral testimony. Each one is cited.'
    )
  }

  // Deep link: ?story=<id> opens that record directly, which is what a citation
  // in a paper or a shared link needs to do.
  const wanted = new URLSearchParams(location.search).get('story')
  if (wanted) onStoryClick(wanted)
}

function buildCommunityChips() {
  const host = document.getElementById('community-filters')
  host.innerHTML = COMMUNITIES.map(
    (c) => `<button class="chip" data-community="${c.id}" aria-pressed="false">${ui.esc(t(c.key))}</button>`
  ).join('')

  host.querySelectorAll('[data-community]').forEach((chip) => {
    chip.onclick = () => {
      const id = chip.dataset.community
      const on = chip.getAttribute('aria-pressed') === 'true'
      chip.setAttribute('aria-pressed', String(!on))
      if (on) state.communities.delete(id)
      else state.communities.add(id)
      refresh()
    }
  })
}

function buildTimelineTicks() {
  const host = document.getElementById('timeline-ticks')
  const marks = [1841, 1880, 1920, 1960, 1990, 2026]
  host.innerHTML = marks
    .map((y) => {
      const pctVal = ((y - YEAR_MIN) / (YEAR_MAX - YEAR_MIN)) * 100
      return `<span style="left:${pctVal.toFixed(2)}%">${y}</span>`
    })
    .join('')
}

function buildBasemapList() {
  const host = document.getElementById('basemap-list')
  const active = basemap()

  host.innerHTML = Object.entries(BASEMAPS).map(([id, bm]) => `
    <label class="layer-row">
      <input type="radio" name="basemap" value="${ui.esc(id)}"${id === active ? ' checked' : ''} />
      <span>${ui.esc(locale() === 'zh' ? bm.label_zh : bm.label_en)}</span>
    </label>`).join('')

  host.querySelectorAll('input[name="basemap"]').forEach((radio) => {
    radio.onchange = () => {
      // The style swap destroys our layers, so the rows have to be pushed back
      // once the new style is up — that is what refresh() does here.
      setBasemap(radio.value, landsdLang(locale()), onStoryClick, () => {
        refresh()
        if (state.selected) setActive(state.selected)
      })
    }
  })
}

function buildCoastlineList() {
  const host = document.getElementById('coastline-list')
  const entries = coastline.layers()

  if (entries.length === 0) {
    host.innerHTML = `<p class="hint" style="margin:0">${
      locale() === 'zh'
        ? '未有歷史海岸線圖層。見 docs/COASTLINES.md 加入圖層。'
        : 'No coastline layers installed yet. See docs/COASTLINES.md to add one.'
    }</p>`
    return
  }

  host.innerHTML = `
    <label class="layer-row">
      <input type="radio" name="coastline" value="" checked />
      <span>${locale() === 'zh' ? '無' : 'None'}</span>
    </label>` +
    entries.map((l) => `
      <label class="layer-row">
        <input type="radio" name="coastline" value="${ui.esc(l.id)}" />
        <span>${ui.esc(locale() === 'zh' ? (l.label_zh ?? l.label_en) : (l.label_en ?? l.label_zh))}</span>
      </label>`).join('')

  host.querySelectorAll('input[name="coastline"]').forEach((radio) => {
    radio.onchange = () => coastline.show(radio.value || null)
  })
}

/* ── Controls ────────────────────────────────────────────────────────────── */

function wireControls() {
  const $ = (id) => document.getElementById(id)

  $('lang-toggle').onclick = () => setLocale(locale() === 'zh' ? 'en' : 'zh')
  $('lang-toggle').textContent = locale() === 'zh' ? 'EN' : '中'

  $('about-btn').onclick = ui.openAbout

  $('rail-toggle').onclick = () => {
    const rail = $('rail')
    const collapsed = rail.classList.toggle('collapsed')
    $('rail-toggle').setAttribute('aria-expanded', String(!collapsed))
  }

  for (const kind of ['stories', 'relocation', 'ecology', 'place']) {
    const key = kind === 'stories' ? 'story' : kind
    $(`layer-${kind}`).onchange = (e) => setKindVisible(key, e.target.checked)
  }

  $('coastline-opacity').oninput = (e) => coastline.setOpacity(Number(e.target.value) / 100)
  $('coastline-swipe').onchange = (e) => coastline.setSwipe(e.target.checked)

  // Two overlapping range inputs behaving as one dual-handle control. Clamping
  // rather than swapping keeps the handle you are dragging under the cursor.
  const from = $('year-from')
  const to = $('year-to')
  const syncYears = () => {
    let a = Number(from.value)
    let b = Number(to.value)
    if (a > b) { if (document.activeElement === from) b = a; else a = b }
    from.value = a; to.value = b
    state.yearFrom = a; state.yearTo = b
    $('timeline-readout').textContent = `${a} – ${b}`

    // Dragging the timeline should also move the shoreline. That coupling is
    // the point of the whole interface: the story and the coast share a date.
    // bestForYear returns null when every surveyed sheet postdates the chosen
    // year. Leaving the previous layer up would then draw, say, a 1960 shoreline
    // while the timeline reads 1950 — a coast that had not been reclaimed yet,
    // presented as if it had. Clear it instead: no shoreline is honest, a
    // too-late one is not.
    const best = coastline.bestForYear(b) || ''
    const radio = document.querySelector(`input[name="coastline"][value="${best}"]`)
    if (radio && !radio.checked) { radio.checked = true; coastline.show(best) }
    refresh()
  }
  from.oninput = syncYears
  to.oninput = syncYears

  $('timeline-reset').onclick = () => {
    from.value = YEAR_MIN; to.value = YEAR_MAX
    syncYears()
  }

  let searchTimer
  $('text-search').oninput = (e) => {
    clearTimeout(searchTimer)
    searchTimer = setTimeout(() => { state.query = e.target.value.trim(); refresh() }, 180)
  }

  $('export-btn').onclick = openExport
  $('add-btn').onclick = () => openSubmitFlow()

  onLocaleChange(() => {
    $('lang-toggle').textContent = locale() === 'zh' ? 'EN' : '中'
    buildCommunityChips()
    buildBasemapList()
    buildCoastlineList()
    // The Lands Department serves a separate label sheet per language, so
    // switching the interface language also reloads the map's own labels.
    setBasemapLang(landsdLang(locale()))
    // Restore chip state after the rebuild.
    state.communities.forEach((id) => {
      document.querySelector(`#community-filters [data-community="${id}"]`)
        ?.setAttribute('aria-pressed', 'true')
    })
    refresh()
    if (state.selected) onStoryClick(state.selected)
  })
}

/* ── Story selection ─────────────────────────────────────────────────────── */

function onStoryClick(id) {
  const s = state.all.find((x) => x.id === id)
  if (!s) return
  state.selected = id
  setActive(id)
  flyToStory(s)
  ui.renderDetail(s, {
    onClose: () => { state.selected = null; setActive(null); ui.closeDetail() },
    onReport: (storyId) => ui.openReport(storyId, doReport),
  })
}

async function doReport(storyId, reason, detail) {
  if (!HAS_BACKEND) throw new Error('No backend configured — reporting is disabled in sample mode.')
  await reportStory(storyId, reason, detail)
  // A privacy or consent report hides the record immediately, so drop it from
  // the local corpus too rather than leaving a ghost pin behind.
  if (reason === 'privacy' || reason === 'consent') {
    state.all = state.all.filter((s) => s.id !== storyId)
    ui.closeDetail()
    setActive(null)
    refresh()
  }
}

/* ── Submission flow ─────────────────────────────────────────────────────── */

const picked = { origin: null, dest: null }
let draft = null

function openSubmitFlow() {
  // Two audiences hit this: a developer running a fresh clone, and a reviewer
  // opening the read-only demo. "Configure Supabase" reads as a broken site to
  // the second one, so say what is true for both — this build cannot save.
  if (!HAS_BACKEND) {
    ui.toast(
      locale() === 'zh'
        ? '此版本只供瀏覽，未連接資料庫，暫時無法新增記錄。'
        : 'This build is read-only — no database is connected, so records cannot be saved yet.',
      true
    )
    return
  }

  ui.openSubmit({
    picked,
    onPick: (which, currentDraft) => {
      draft = currentDraft
      startPicking(which)
    },
    onSubmit: doSubmit,
  })

  if (draft) {
    ui.restore(document.querySelector('.modal'), draft)
  }
}

/** Suspend the form, let the visitor click the map, then bring the form back
    with everything they had already typed still in place. */
function startPicking(which) {
  ui.suspendModal()
  map.getCanvas().style.cursor = 'crosshair'

  const fab = document.getElementById('add-btn')
  fab.classList.add('picking')
  ui.toast(which === 'origin' ? t('click_map_origin') : t('click_map_dest'))

  const handler = (e) => {
    picked[which] = { lng: e.lngLat.lng, lat: e.lngLat.lat }
    map.off('click', handler)
    map.getCanvas().style.cursor = ''
    fab.classList.remove('picking')
    ui.closeModal()
    openSubmitFlow()
  }
  map.on('click', handler)
}

async function doSubmit(formData, files) {
  const payload = {
    ...formData,
    origin_lng: picked.origin.lng,
    origin_lat: picked.origin.lat,
    dest_lng: picked.dest?.lng ?? null,
    dest_lat: picked.dest?.lat ?? null,
  }
  delete payload._consent1
  delete payload._consent2

  // Media first: if an upload fails we want to fail before a partial record
  // exists on the map, not after.
  if (files.audio) payload.audio_path = await uploadFile(files.audio, 'audio')
  if (files.photos.length) {
    payload.photo_paths = []
    for (const photo of files.photos) {
      payload.photo_paths.push(await uploadFile(photo, 'photo'))
    }
  }

  const saved = await submitStory(payload)

  ui.closeModal()
  ui.toast(t('submit_ok'))

  picked.origin = null
  picked.dest = null
  draft = null

  if (saved?.story) {
    state.all.unshift(saved.story)
    refresh()
    onStoryClick(saved.story.id)
  } else {
    state.all = await fetchStories()
    refresh()
  }
}

/* ── Export ──────────────────────────────────────────────────────────────── */

function openExport() {
  const rows = visible()
  const zh = locale() === 'zh'
  ui.openModal(`
    <h2>${zh ? '匯出資料' : 'Export data'}</h2>
    <p class="lede">${zh
      ? `按目前篩選匯出 ${rows.length} 筆記錄。內容以 CC BY-SA 4.0 授權，請註明出處。`
      : `Exports the ${rows.length} records matching your current filters. Content is licensed CC BY-SA 4.0 — please attribute.`}</p>
    <p>${zh
      ? 'GeoJSON 可直接在 QGIS 開啟，遷徙記錄會匯出為線段。CSV 適合 Excel 或統計軟件。'
      : 'GeoJSON opens directly in QGIS, with relocations exported as lines. CSV suits Excel or statistical software.'}</p>
    <div class="modal-actions">
      <button class="ghost" data-act="cancel">${ui.esc(t('cancel'))}</button>
      <span class="spacer"></span>
      <button class="ghost" data-act="csv">CSV</button>
      <button class="btn-primary" data-act="geojson">GeoJSON</button>
    </div>`,
  { onMount: (m) => {
      m.querySelector('[data-act="cancel"]').onclick = ui.closeModal
      m.querySelector('[data-act="geojson"]').onclick = () => {
        download('tideline.geojson', JSON.stringify(toGeoJSON(rows), null, 2), 'application/geo+json')
        ui.closeModal()
      }
      m.querySelector('[data-act="csv"]').onclick = () => {
        download('tideline.csv', toCSV(rows), 'text/csv;charset=utf-8')
        ui.closeModal()
      }
    } })
}

/* boot() is a long async chain — map init, manifest fetch, data fetch. An
   unhandled rejection anywhere in it leaves a half-built page with no error
   anywhere the user can see it: the rail renders, nothing responds, and the
   console shows only an uncaught promise. Say what broke instead. */
boot().catch(err => {
  console.error('[tideline] boot failed:', err)
  ui.toast(
    (locale() === 'zh' ? '載入失敗：' : 'Failed to load: ') + (err?.message || err),
    true
  )
})
